// client/src/coms/e2ee/E2eeSettings.js
// Personal Settings -> Privacy & Security -> End-to-End Encryption.
// - The switch never changes the preference by itself: a confirmation explains what will happen first.
// - Turning it on sets up encryption on this browser first: on the account's first device that creates the
//   encryption identity and a recovery key (shown once, to be saved by the user); on a further device the user
//   verifies it with that recovery key, which also restores message history from the encrypted key backup.
// - The private keys are created here and never leave the user's devices unencrypted; the recovery key is never
//   sent to Voxemera.
import React, { useCallback, useEffect, useState } from 'react';
import { comsApiGet, comsApiPut } from '../comsApi';
import { Row, Switch } from '../personalisation/settingsControls';
import { cryptoAvailable, currentDeviceId, deviceTrust, ensureDevice, listDevices, removeDevice, restoreWithRecoveryKey, setupIdentity } from './cryptoSession';
import './e2ee.css';

function apiError(e) {
  const d = e && e.data;
  return (d && d.error) || String((e && e.message) || e);
}

function fingerprint(key) {
  return String(key || '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

export function RecoveryKeyPanel({ recoveryKey, onDone }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div className="vx-e2ee-recovery" role="alertdialog" aria-label="Your recovery key" data-testid="settings-e2ee-recovery">
      <strong>Save your recovery key</strong>
      <span>
        You need it to read encrypted messages on a new device or after clearing this browser. Voxemera does not have a copy and cannot recover it for you.
        Store it in a password manager or somewhere safe; anyone who has it can read your encrypted messages.
      </span>
      <code data-testid="settings-e2ee-recovery-key">{fingerprint(recoveryKey)}</code>
      <div className="vx-e2ee-confirm-row">
        <button
          type="button"
          className="vx-btn small"
          onClick={() => {
            try {
              navigator.clipboard.writeText(recoveryKey).then(() => setCopied(true));
            } catch {}
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <label>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="settings-e2ee-recovery-saved" /> I have saved my recovery key
      </label>
      <div className="vx-e2ee-confirm-row">
        <button type="button" className="vx-btn small" disabled={!saved} onClick={onDone} data-testid="settings-e2ee-recovery-done">
          Continue
        </button>
      </div>
    </div>
  );
}

export default function E2eeSettings() {
  const [enabled, setEnabled] = useState(null);
  const [mx, setMx] = useState('');
  const [confirming, setConfirming] = useState(null); // the value awaiting confirmation, or null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [devices, setDevices] = useState([]);
  const [trust, setTrust] = useState(null); // { identity, deviceSigned, serverIdentity }
  const [recoveryKey, setRecoveryKey] = useState(''); // shown once after setup or reset
  const [afterRecovery, setAfterRecovery] = useState(null); // what to do once the key was saved
  const [restoreInput, setRestoreInput] = useState('');
  const [resetting, setResetting] = useState(false);
  const supported = cryptoAvailable();

  const loadDevices = useCallback(() => {
    listDevices()
      .then((r) => setDevices((r && r.devices) || []))
      .catch(() => {});
  }, []);

  const loadTrust = useCallback(
    (who) =>
      who && supported
        ? deviceTrust(who)
            .then(setTrust)
            .catch(() => {})
        : Promise.resolve(),
    [supported]
  );

  useEffect(() => {
    let cancelled = false;
    comsApiGet('/e2ee/preference')
      .then((r) => {
        if (cancelled) return;
        setEnabled(!!(r && r.dm_enabled));
        const who = String((r && r.user_id_mx) || '');
        setMx(who);
        // Only open the encryption device here if this account already uses encryption.
        if (r && (r.dm_enabled || r.devices)) loadTrust(who);
      })
      .catch((e) => !cancelled && setError(`Could not load your encryption setting: ${apiError(e)}`));
    loadDevices();
    return () => {
      cancelled = true;
    };
  }, [loadDevices, loadTrust]);

  const savePref = async (next) => {
    const r = await comsApiPut('/e2ee/preference', { dm_enabled: next, confirm: true });
    setEnabled(!!(r && r.dm_enabled));
    setConfirming(null);
    const n = Number((r && r.notified_threads) || 0);
    setSaved(
      r && r.changed
        ? `Saved. ${n ? `A notice was posted in ${n} conversation${n === 1 ? '' : 's'} so the other participants know.` : 'You have no direct messages yet.'}`
        : 'No change was needed.'
    );
    loadDevices();
    loadTrust(mx);
  };

  const save = async (next) => {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      if (next) {
        let t;
        try {
          await ensureDevice(mx, { replaceRemoved: true });
          t = await deviceTrust(mx);
        } catch (e) {
          setError(`Encryption was not turned on: this device could not set up its encryption keys (${String((e && e.message) || e)}).`);
          return;
        }
        setTrust(t);
        if (!t.serverIdentity) {
          // First device of this account: create the identity and show the recovery key before saving.
          const key = await setupIdentity(mx);
          setRecoveryKey(key);
          loadTrust(mx);
          setAfterRecovery(() => () => savePref(true));
          return;
        }
        if (!t.deviceSigned) {
          setError('Your account already has an encryption identity. Verify this device with your recovery key (below) first.');
          return;
        }
      }
      await savePref(next);
    } catch (e) {
      setError(`Your encryption setting was not changed: ${apiError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setError('');
    setSaved('');
    try {
      const r = await restoreWithRecoveryKey(mx, restoreInput);
      setRestoreInput('');
      setSaved(`This device is verified.${r && r.restored ? ` ${r.restored} message key${r.restored === 1 ? '' : 's'} restored from your key backup.` : ''}`);
      await loadTrust(mx);
      loadDevices();
    } catch (e) {
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setError('');
    try {
      const key = await setupIdentity(mx, { reset: true });
      setResetting(false);
      setRecoveryKey(key);
      setAfterRecovery(() => () => loadTrust(mx));
    } catch (e) {
      setError(`The identity was not reset: ${String((e && e.message) || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (deviceId) => {
    setError('');
    try {
      await removeDevice(deviceId);
      loadDevices();
    } catch (e) {
      setError(`The device was not removed: ${apiError(e)}`);
    }
  };

  const thisDevice = currentDeviceId();
  const needsRestore = !!(trust && trust.serverIdentity && !trust.deviceSigned);

  return (
    <div id="vx-setting-e2ee" data-setting="e2ee" className="vx-set-block">
      <h4 className="vx-set-subtitle">End-to-End Encryption</h4>
      <Row
        label="End-to-end encryption for direct messages"
        hint="Off by default. A direct message is only encrypted when both participants turn this on."
        settingId="e2ee-dm"
        scope="account"
      >
        <Switch
          label="End-to-end encryption for direct messages"
          checked={!!enabled}
          onChange={(v) => enabled !== null && !busy && !recoveryKey && (setSaved(''), setConfirming(v))}
          testId="settings-e2ee-dm"
        />
      </Row>

      {confirming !== null && !recoveryKey ? (
        <div className="vx-e2ee-confirm" role="alertdialog" aria-label="Confirm encryption change" data-testid="settings-e2ee-confirm">
          {confirming ? (
            <>
              <strong>Turn on end-to-end encryption?</strong>
              <span>
                This browser sets up your encryption keys. A direct message becomes end-to-end encrypted only when <em>both</em> participants have this setting
                on; until then it stays standard, and its header says so.
              </span>
              <span>
                If this is your first encrypted device you will get a <strong>recovery key</strong>: you need it to use encryption on other devices and to read
                your encrypted history after clearing this browser. Voxemera never receives it.
              </span>
              <span>Everyone you have a direct message with will see a notice in that conversation that you turned encryption on.</span>
            </>
          ) : (
            <>
              <strong>Turn off end-to-end encryption?</strong>
              <span>Future direct messages will be standard (not end-to-end encrypted) in every conversation, including ones where the other participant has encryption on.</span>
              <span>Messages already sent keep the encryption status they were sent with. Everyone you have a direct message with will see a notice, and must acknowledge it before they can message you again.</span>
            </>
          )}
          <div className="vx-e2ee-confirm-row">
            <button type="button" className="vx-btn small" onClick={() => save(confirming)} disabled={busy || (confirming && !supported)} data-testid="settings-e2ee-confirm-btn">
              {busy ? 'Working…' : confirming ? 'Turn on' : 'Turn off'}
            </button>
            <button type="button" className="vx-btn small" onClick={() => setConfirming(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {recoveryKey ? (
        <RecoveryKeyPanel
          recoveryKey={recoveryKey}
          onDone={async () => {
            const next = afterRecovery;
            setRecoveryKey('');
            setAfterRecovery(null);
            setBusy(true);
            try {
              if (typeof next === 'function') await next();
            } catch (e) {
              setError(`Your encryption setting was not changed: ${apiError(e)}`);
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}

      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}
      {saved ? (
        <p className="vx-set-hint" role="status" data-testid="settings-e2ee-saved">
          {saved}
        </p>
      ) : null}
      {!supported ? (
        <p className="vx-set-hint" data-testid="settings-e2ee-unsupported">
          This browser cannot run end-to-end encryption (it needs WebAssembly and IndexedDB), so it cannot be turned on here.
        </p>
      ) : null}

      {trust ? (
        <div className="vx-e2ee-explain" data-testid="settings-e2ee-this-device">
          <strong>This browser</strong>
          {trust.deviceSigned ? (
            <span data-testid="settings-e2ee-device-verified">✅ Verified with your encryption identity. It can send and read encrypted messages.</span>
          ) : needsRestore ? (
            <>
              <span data-testid="settings-e2ee-device-unverified">
                ⚠️ Not verified yet. Enter your recovery key to verify this browser and restore your encrypted message history. Until then it cannot send or
                read encrypted messages, and other people’s devices do not share keys with it.
              </span>
              <input
                className="vx-e2ee-input"
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="Recovery key"
                value={restoreInput}
                onChange={(e) => setRestoreInput(e.target.value)}
                data-testid="settings-e2ee-restore-input"
              />
              <div className="vx-e2ee-confirm-row">
                <button type="button" className="vx-btn small" disabled={busy || !restoreInput.trim()} onClick={restore} data-testid="settings-e2ee-restore-btn">
                  {busy ? 'Verifying…' : 'Verify this browser'}
                </button>
                <button type="button" className="vx-btn small" disabled={busy} onClick={() => setResetting(true)}>
                  I lost my recovery key
                </button>
              </div>
            </>
          ) : (
            <span>Encryption is not set up on this account yet.</span>
          )}
          {trust.serverIdentity && trust.deviceSigned && !resetting ? (
            <div className="vx-e2ee-confirm-row">
              <button type="button" className="vx-btn small" disabled={busy} onClick={() => setResetting(true)}>
                Reset encryption identity…
              </button>
            </div>
          ) : null}
          {resetting ? (
            <div className="vx-e2ee-ack" data-testid="settings-e2ee-reset">
              <strong>Reset your encryption identity?</strong>
              <span>
                You get a new identity and a new recovery key. Everyone you talk to will be told your identity changed and must accept it before they can send
                you encrypted messages again; people who verified you must verify you again. Encrypted history this browser cannot already read stays
                unreadable. Only do this if you lost your recovery key or think it was exposed.
              </span>
              <div className="vx-e2ee-ack-row">
                <button type="button" className="vx-btn small" disabled={busy} onClick={reset} data-testid="settings-e2ee-reset-confirm">
                  {busy ? 'Working…' : 'Reset identity'}
                </button>
                <button type="button" className="vx-btn small" disabled={busy} onClick={() => setResetting(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="vx-e2ee-explain">
        <strong>How mutual encryption works</strong>
        <table>
          <thead>
            <tr>
              <th>You</th>
              <th>Them</th>
              <th>Direct message</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Off</td><td>Off</td><td>Standard</td></tr>
            <tr><td>On</td><td>Off</td><td>Standard, with a warning in the header</td></tr>
            <tr><td>Off</td><td>On</td><td>Standard</td></tr>
            <tr><td>On</td><td>On</td><td>End-to-end encrypted, once your devices have set up the session</td></tr>
          </tbody>
        </table>
        <span>
          Messages are encrypted with Olm and Megolm (the Matrix.org crypto library, vodozemac). Keys only go to devices signed by their owner’s encryption
          identity, so a device added to someone’s account without their recovery key gets nothing. You can verify a contact by comparing safety codes; if
          a contact’s identity changes, nothing is sent to them until you review it. A lock only appears once this browser has set up the encrypted session.
        </span>
        <span>
          <strong>Beta:</strong> this implementation has not been independently security reviewed. Encrypted messages cannot carry attachments or stickers,
          or be edited, yet. Messages restored from the key backup are shown as “sender not confirmed”.
        </span>
      </div>

      {devices.length ? (
        <div className="vx-e2ee-explain" data-testid="settings-e2ee-devices">
          <strong>Your encryption devices</strong>
          {devices.map((d) => (
            <div key={d.device_id} className="vx-e2ee-device">
              <span>
                <code>{d.device_id}</code>
                {d.device_id === thisDevice ? ' (this browser)' : ''}
              </span>
              <span className="vx-e2ee-fp" title="Public signing key">
                {fingerprint(d.ed25519)}
              </span>
              <span className="vx-set-hint">Last active {String(d.last_seen_at || '').replace('T', ' ').slice(0, 16)} UTC</span>
              {d.device_id !== thisDevice ? (
                <button type="button" className="vx-btn small" onClick={() => remove(d.device_id)}>
                  Remove
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
